import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "services/api.service";
import { Employee } from "types/employee";

export type UseEmployeesOptions = {
  /** When true, `?search=` from the URL is sent to the API (employees list page only). */
  enableListSearch?: boolean;
};

export default function useEmployees(options: UseEmployeesOptions = {}) {
  const { enableListSearch = false } = options;
  const [searchParams] = useSearchParams();
  const search = useMemo(() => {
    if (!enableListSearch) return "";
    return (searchParams.get("search") ?? "").trim();
  }, [searchParams, enableListSearch]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const refetch = useCallback(() => {
    setIsLoading(true);
    setIsError(false);
    const q = search ? `?search=${encodeURIComponent(search)}` : "";
    return api
      .get<{ employees: Employee[] }>(`/employees${q}`)
      .then(({ data }) => {
        setEmployees(data.employees || []);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [search]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { employees, isLoading, isError, refetch };
}
